import { QuestionIcon } from '@phosphor-icons/react'
import { createFormHook, createFormHookContexts, useSelector } from '@tanstack/react-form'
import { useCallback, useState } from 'react'
import { match } from 'ts-pattern'
import { useGetScaleForTool } from '../../utils/api-client/api-hooks.ts'
import { CreateWorkflowRequest, Scale, ToolSchema, type Tool } from '../../utils/api-client/api-schemas.ts'
import { getWorkflowTools } from '../../utils/detector-utils.ts'
import { getToolName, sortToolsByName } from '../../utils/repository-utils.tsx'
import { getSeveritiesByRankDescending } from '../../utils/scale-utils.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { InputContainer } from '../input/input-container'
import { InputGroup } from '../input/input-group'
import { SelectBox } from '../input/select-box.tsx'
import { Slider } from '../input/slider'
import { TextInput } from '../input/text-input'
import { ToolIcon } from '../third-party-branding/tool-icon.tsx'
import { TooltipWithHover } from '../tooltip'
import { FixesCustomizationSelector, SeveritySelector } from './workflow-form-controls.tsx'
import * as styles from './workflow-form.css'

export type CommonWorkflowParams = {
  name?: string
  tool: Tool
  selectedSeverities?: string[]
  minimumSeverityScore?: number
  maximumSeverityScore?: number
  fixConfidence?: 'high' | 'medium' | 'low'
}

export type WorkflowParams = CommonWorkflowParams & {
  selectedAction: WorkflowActionType
} & (
    | {
        type: 'scan' | 'pull-request-scan'
        branch: string
        limitFixes: boolean
        fixesLimit?: number
      }
    | {
        type: 'scheduled'
        branch: string
        cadence: 'daily' | 'weekly'
      }
  )

export type WorkflowType = WorkflowParams['type']

export type WorkflowActionType = CreateWorkflowRequest['action']['type']

export type CreateWorkflowFormValues = {
  selectedType: WorkflowType | undefined
  selectedTool: Tool | undefined
  selectedCadence: string | undefined
  workflowName: string
  branch: string
  limitFixes: boolean
  fixesLimit: number
  selectedSeverities: string[]
  selectedRange: [number, number]
  fixConfidence: 'high' | 'medium' | 'low' | undefined
  selectedAction: WorkflowActionType
}

const FORM_DEFAULT_VALUES: CreateWorkflowFormValues = {
  selectedType: undefined,
  selectedTool: undefined,
  selectedCadence: undefined,
  workflowName: '',
  branch: '',
  limitFixes: true,
  fixesLimit: 1,
  selectedSeverities: [],
  selectedRange: [0, 10],
  fixConfidence: undefined,
  selectedAction: 'create-patch',
}

const { fieldContext, formContext } = createFormHookContexts()

const { useAppForm, useTypedAppFormContext } = createFormHook({
  fieldComponents: {},
  formComponents: {},
  fieldContext,
  formContext,
})

export const useCreateWorkflowForm = (defaultValueOverrides: Partial<CreateWorkflowFormValues> = {}) =>
  useAppForm({
    defaultValues: {
      ...FORM_DEFAULT_VALUES,
      ...defaultValueOverrides,
    },
  })

const useFormContext = () => useTypedAppFormContext({ defaultValues: FORM_DEFAULT_VALUES })

export const CreateWorkflowForm: React.FC = () => {
  const form = useFormContext()

  const selectedType = useSelector(form.store, state => state.values.selectedType)
  const selectedTool = useSelector(form.store, state => state.values.selectedTool)
  const selectedCadence = useSelector(form.store, state => state.values.selectedCadence)
  const workflowName = useSelector(form.store, state => state.values.workflowName)
  const branch = useSelector(form.store, state => state.values.branch)
  const selectedSeverities = useSelector(form.store, state => state.values.selectedSeverities)
  const selectedRange = useSelector(form.store, state => state.values.selectedRange)
  const fixConfidence = useSelector(form.store, state => state.values.fixConfidence)
  const limitFixes = useSelector(form.store, state => state.values.limitFixes)
  const fixesLimit = useSelector(form.store, state => state.values.fixesLimit)
  const selectedAction = useSelector(form.store, state => state.values.selectedAction)

  const { data: scale } = useGetScaleForTool(selectedTool)

  const severitySection = match(scale)
    .with({ type: 'ranked-set' }, rankedSet => (
      <SeveritySelector
        severities={getSeveritiesByRankDescending(rankedSet)}
        selectedSeverities={selectedSeverities}
        setSelectedSeverities={updater => {
          const next = typeof updater === 'function' ? updater(selectedSeverities) : updater
          form.setFieldValue('selectedSeverities', next)
        }}
      />
    ))
    .with({ type: 'score' }, score => (
      <div className={styles.severityContainer}>
        <InputContainer label="Severity range" explicitLabelCondition="optional">
          <Slider
            min={score.min ?? 0}
            max={score.max ?? 10}
            value={selectedRange}
            onValueChange={value => form.setFieldValue('selectedRange', value)}
            aria-label="Severity threshold range"
          />
        </InputContainer>
      </div>
    ))
    .with(undefined, () => null)
    .exhaustive()

  const fixesCustomizationSelector = (
    <FixesCustomizationSelector
      title="Customize the fixes you receive"
      severitySection={severitySection}
      fixConfidence={fixConfidence}
      setFixConfidence={updater => {
        const next = typeof updater === 'function' ? updater(fixConfidence) : updater
        form.setFieldValue('fixConfidence', next)
      }}
      limitFixes={limitFixes}
      setLimitFixes={updater => {
        const next = typeof updater === 'function' ? updater(limitFixes) : updater
        form.setFieldValue('limitFixes', next)
      }}
      fixesLimit={fixesLimit}
      setFixesLimit={updater => {
        const next = typeof updater === 'function' ? updater(fixesLimit) : updater
        form.setFieldValue('fixesLimit', next)
      }}
    />
  )

  const handleToolChange = useCallback(
    (value: string) => {
      form.setFieldValue('selectedTool', ToolSchema.parse(value))
      form.setFieldValue('selectedSeverities', [])
    },
    [form]
  )

  const handleTypeChange = useCallback(
    (value: string) => {
      const type = value as WorkflowType
      form.setFieldValue('selectedType', type)
      // Analyzing pull requests should not cap the number of fixes per PR by default.
      form.setFieldValue('limitFixes', type !== 'pull-request-scan')
    },
    [form]
  )

  return (
    <form className={styles.workflowFormContainer}>
      <InputGroup title="What would you like to automate?">
        <SelectBox
          label="Workflow"
          name="workflowType"
          placeholder="Select workflow"
          value={selectedType}
          onValueChange={handleTypeChange}
          options={
            [
              { value: 'scheduled', label: 'Schedule fixes' },
              { value: 'scan', label: 'Analyze new scans' },
              { value: 'pull-request-scan', label: 'Analyze pull requests' },
            ] satisfies { value: WorkflowType; label: string }[]
          }
        />
      </InputGroup>

      {selectedType === 'scheduled' && (
        <>
          <div className={styles.findingsContainer}>
            <div className={styles.sectionHeading}>Which findings are you monitoring?</div>
            <SelectBox
              label="Tool"
              name="tool"
              value={selectedTool}
              onValueChange={handleToolChange}
              options={sortToolsByName(getWorkflowTools()).map(tool => {
                const name = getToolName(tool)
                return {
                  value: tool,
                  label: (
                    <div className={styles.toolOptionLabel}>
                      <ToolIcon tool={tool} size="medium" />
                      {name}
                    </div>
                  ),
                }
              })}
              placeholder="Select tool"
            />
          </div>

          <InputGroup title="Which branch(es) are you targeting?">
            <BranchPrefixInput branch={branch} setBranch={value => form.setFieldValue('branch', value)} />
          </InputGroup>

          {fixesCustomizationSelector}

          <div className={styles.findingsContainer}>
            <div className={styles.sectionHeading}>Define a schedule</div>
            <SelectBox
              label="Cadence"
              name="cadence"
              value={selectedCadence}
              onValueChange={value => form.setFieldValue('selectedCadence', value)}
              options={[
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
              ]}
              placeholder="Select cadence"
            />
          </div>

          <div className={styles.flexContainer}>
            <TextInput
              id="workflowName"
              name="workflowName"
              label="Name this workflow"
              value={workflowName}
              onChange={value => form.setFieldValue('workflowName', value)}
              placeholder="Override the default workflow name with your own"
              explicitLabelCondition="optional"
            />
          </div>
        </>
      )}

      {(selectedType === 'scan' || selectedType === 'pull-request-scan') && (
        <div className={styles.scanSectionContainer}>
          <div className={styles.sectionGroup}>
            <div className={styles.sectionHeading}>Which findings are you monitoring?</div>
            <SelectBox
              label="Tool"
              name="tool"
              value={selectedTool}
              onValueChange={handleToolChange}
              options={sortToolsByName(getWorkflowTools()).map(tool => {
                const name = getToolName(tool)
                return {
                  value: tool,
                  label: (
                    <div className={styles.toolOptionLabel}>
                      <ToolIcon tool={tool} size="medium" />
                      {name}
                    </div>
                  ),
                }
              })}
              placeholder="Select tool"
            />
          </div>

          <InputGroup
            title={
              selectedType === 'pull-request-scan'
                ? 'Which branch are you comparing against?'
                : 'Which branch(es) are you targeting?'
            }
          >
            <BranchPrefixInput
              branch={branch}
              setBranch={value => form.setFieldValue('branch', value)}
              helperText={
                selectedType === 'pull-request-scan'
                  ? 'The base branch pull requests are opened against. Use * for prefix matching. Default branch used if empty.'
                  : undefined
              }
            />
          </InputGroup>

          <div className={styles.sectionGroup}>
            <div className={styles.sectionHeading}>Configure the action to take</div>
            <SelectBox
              label="Action"
              value={selectedAction}
              onValueChange={value => form.setFieldValue('selectedAction', value as WorkflowActionType)}
              options={
                [
                  { value: 'none', label: 'Analyze only' },
                  { value: 'create-patch', label: 'Analyze and fix' },
                ] satisfies { value: WorkflowActionType; label: string }[]
              }
            />
            {selectedAction === 'create-patch' && fixesCustomizationSelector}
          </div>

          <div className={styles.flexContainer}>
            <TextInput
              id="workflowName"
              name="workflowName"
              label="Name this workflow"
              value={workflowName}
              onChange={value => form.setFieldValue('workflowName', value)}
              placeholder="Override our default workflow name with your own"
              explicitLabelCondition="optional"
            />
          </div>
        </div>
      )}
    </form>
  )
}

type BranchPrefixInputProps = {
  branch: string
  setBranch: (value: string) => unknown
  helperText?: string
}

const BranchPrefixInput = ({ branch, setBranch, helperText }: BranchPrefixInputProps) => {
  const [isTouched, setIsTouched] = useState(false)
  const hasTrailingSlashError = branch.endsWith('/')
  const showError = isTouched && hasTrailingSlashError

  return (
    <div className={styles.flexContainer}>
      <TextInput
        id="branch"
        name="branch"
        label="Branch / Prefix"
        value={branch}
        onChange={setBranch}
        onBlur={() => setIsTouched(true)}
        placeholder="Enter a branch name or prefix pattern (requires *)"
        explicitLabelCondition="optional"
        errorText={showError ? "A trailing '*' is required for prefix pattern matching" : undefined}
        isError={showError}
      />
      {!showError && (
        <div className={styles.branchHelperRow}>
          <span>
            {helperText ??
              'Exact match by default. Use * to match multiple branches. Default branch will be used if nothing is specified.'}
          </span>
          <TooltipWithHover
            side="top"
            trigger={
              <span className={styles.branchTooltipIcon} aria-label="Branch pattern examples">
                <QuestionIcon size={14} weight="bold" />
              </span>
            }
          >
            <div className={styles.branchTooltipContent}>
              <div>
                <strong>main</strong> <em>(exact match; indicates a single branch)</em>
              </div>
              <div>
                <strong>feature/*</strong> <em>(prefix; indicates multiple branches)</em>
              </div>
            </div>
          </TooltipWithHover>
        </div>
      )}
    </div>
  )
}

export const getDefaultWorkflowName = (tool: Tool | undefined, cadence: string | undefined) => {
  if (!tool || !cadence) {
    return ''
  }
  const toolName = getToolName(tool)
  return `${cadence === 'daily' ? 'Daily' : 'Weekly'} ${toolName} fixes`
}

const getWorkflowParams = (values: CreateWorkflowFormValues, scale: Scale | undefined): WorkflowParams | undefined => {
  const {
    selectedType,
    selectedTool,
    selectedCadence,
    workflowName,
    branch,
    limitFixes,
    fixesLimit,
    selectedSeverities,
    selectedRange,
    fixConfidence,
    selectedAction,
  } = values

  if (!selectedTool || !selectedType || (selectedType === 'scheduled' && !selectedCadence)) {
    return undefined
  }

  const commonParams: CommonWorkflowParams = {
    name: workflowName || undefined,
    tool: selectedTool,
    selectedSeverities,
    fixConfidence,
  }

  if (scale?.type === 'score') {
    if (selectedRange[0] !== (scale.min ?? 0)) {
      commonParams.minimumSeverityScore = selectedRange[0]
    }
    if (selectedRange[1] !== (scale.max ?? 10)) {
      commonParams.maximumSeverityScore = selectedRange[1]
    }
  }

  return match(selectedType)
    .with(
      'scan',
      'pull-request-scan',
      type =>
        ({
          ...commonParams,
          type,
          branch,
          limitFixes,
          fixesLimit: limitFixes ? fixesLimit : undefined,
          selectedAction,
        }) satisfies WorkflowParams
    )
    .with(
      'scheduled',
      type =>
        ({
          ...commonParams,
          type,
          branch,
          cadence: selectedCadence as 'daily' | 'weekly',
          selectedAction,
        }) satisfies WorkflowParams
    )
    .otherwise(() => undefined)
}

type CreateWorkflowButtonProps = {
  onCreateWorkflow: (params: WorkflowParams) => unknown
}

export const CreateWorkflowButton: React.FC<CreateWorkflowButtonProps> = ({ onCreateWorkflow }) => {
  const form = useFormContext()
  const values = useSelector(form.store, state => state.values)
  const { data: scale } = useGetScaleForTool(values.selectedTool)
  const isBranchEndingWithSlash = values.branch.endsWith('/')

  const state = match(values.selectedType)
    .when(
      () => isBranchEndingWithSlash,
      () => 'disabled' as const
    )
    .with('scan', 'pull-request-scan', () => (values.selectedTool ? 'default' : 'disabled'))
    .with('scheduled', () => (values.selectedTool && values.selectedCadence ? 'default' : 'disabled'))
    .otherwise(() => 'disabled' as const)

  return (
    <DefaultButton
      buttonType="primary"
      state={state}
      buttonRole="submit"
      onClick={() => {
        const params = getWorkflowParams(values, scale)
        if (params) {
          onCreateWorkflow(params)
        }
      }}
      data-testid="workflow-drawer-create-workflow-button"
    >
      Create workflow
    </DefaultButton>
  )
}

type ManageWorkflowsFooterButtonProps = {
  onDirtyClick: () => unknown
  onCleanClick: () => unknown
  children: React.ReactNode
}

export const ManageWorkflowsFooterButton: React.FC<ManageWorkflowsFooterButtonProps> = ({
  onDirtyClick,
  onCleanClick,
  children,
}) => {
  const form = useFormContext()
  const selectedType = useSelector(form.store, state => state.values.selectedType)
  const selectedTool = useSelector(form.store, state => state.values.selectedTool)
  const selectedCadence = useSelector(form.store, state => state.values.selectedCadence)

  return (
    <GhostButton
      type="default"
      onClick={e => {
        e.preventDefault()
        const isDirty =
          selectedType === 'scan' || selectedType === 'pull-request-scan'
            ? !!selectedTool
            : !!(selectedTool && selectedCadence)
        if (isDirty) {
          onDirtyClick()
        } else {
          onCleanClick()
        }
      }}
      data-testid="workflow-drawer-manage-workflows-button"
    >
      {children}
    </GhostButton>
  )
}

export const toCreateWorkflowRequest = (params: WorkflowParams): CreateWorkflowRequest => {
  const event = (
    params.type === 'scheduled'
      ? {
          type: 'schedule' as const,
          cadence: params.cadence,
          start: undefined,
          branch: params.branch || undefined,
        }
      : params.type === 'scan'
        ? {
            type: 'new-scan' as const,
            branch: params.branch || undefined,
          }
        : {
            type: 'pull-request-scan' as const,
            target_branch: params.branch || undefined,
          }
  ) satisfies CreateWorkflowRequest['event']

  if (params.selectedAction === 'none') {
    return {
      tool: params.tool,
      name: params.name || undefined,
      event,
      action: { type: 'none' },
    } satisfies CreateWorkflowRequest
  }

  const isScanType = params.type === 'scan' || params.type === 'pull-request-scan'

  return {
    tool: params.tool,
    name: params.name || undefined,
    event,
    action: {
      type: 'create-patch',
      findings: {
        include_severity_labels: params.selectedSeverities?.length ? params.selectedSeverities : undefined,
        minimum_severity_score: params.minimumSeverityScore,
        maximum_severity_score: params.maximumSeverityScore,
      },
      fixes: params.fixConfidence
        ? {
            minimum_fix_confidence: params.fixConfidence,
            max_count: 1,
          }
        : undefined,
      limit:
        isScanType && params.limitFixes && params.fixesLimit
          ? {
              type: 'finding-count',
              max: params.fixesLimit,
            }
          : undefined,
    },
  } satisfies CreateWorkflowRequest
}
